import { IfAbility } from '@efdigital/ll-addon-auth';
import { Heading } from '@efdigital/ll-addon-ui';
import Pagination from '@efdigital/ll-admin-v2/components/media/pagination.gts';
import SearchClients, {
  type SearchClientsState,
} from '@efdigital/ll-admin-v2/components/search/search-clients.gts';
import eq from '@efdigital/ll-admin-v2/helpers/eq.ts';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Value } from '@warp-drive/core/types/json/raw';
import { Request } from '@warp-drive/ember';

function getTotal(page: Value | undefined): number {
  if (typeof page === 'object' && page !== null && 'total' in page) {
    return page.total as number;
  }
  return 0;
}

export default class LibraryClientsIndex extends Component {
  @tracked state: SearchClientsState = {
    title: '',
    subscription: '',
    view: '',
    domain: '',
    connection: '',
    username: '',
    expires: '',
    sortField: 'title',
    page: 1,
    pageSize: 30,
  };

  setState = (partial: Partial<SearchClientsState>) => {
    this.state = { ...this.state, ...partial };
  };

  setPage = (page: number): void => {
    this.state = { ...this.state, page };
  };

  formatDate(dateString: string | undefined): string {
    if (!dateString) {
      return '';
    }
    return new Date(dateString).toLocaleDateString();
  }

  isExpired(expiresDate: string | undefined): boolean {
    if (!expiresDate) {
      return false;
    }
    return new Date(expiresDate) < new Date();
  }

  expiresSoon(expiresDate: string | undefined): boolean {
    if (!expiresDate) {
      return false;
    }
    const expires = new Date(expiresDate);
    const now = new Date();
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(now.getDate() + 7);
    return expires >= now && expires <= sevenDaysFromNow;
  }

  <template>
    <div class="container py-5">
      <Heading
        @text="Client Library"
        @secondary="Browse and manage clients. Filter by title, subscription type, view, domains, and more."
      />

      <IfAbility @ability="clients" @atLeast="viewer">
        <:if>
          <SearchClients @state={{this.state}} @setState={{this.setState}}>
            <:content as |query|>
              {{! Results }}
              <Request @query={{query}}>
                <:content as |response|>
                  {{#if response.data.length}}
                    <div class="mb-3">
                      <p class="text-muted">
                        Showing
                        {{response.data.length}}
                        of
                        {{getTotal response.meta.page}}
                        clients
                      </p>
                    </div>

                    <div class="table-responsive">
                      <table class="table table-striped table-hover">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Subscription</th>
                            <th>Expires</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {{#each response.data as |client|}}
                            <tr
                              class={{if
                                (this.isExpired client.expires)
                                "table-danger"
                                (if
                                  (this.expiresSoon client.expires)
                                  "table-warning"
                                )
                              }}
                            >
                              <td>
                                <LinkTo
                                  @route="library.clients.client.edit.general"
                                  @model={{client.id}}
                                  class="fw-bold text-decoration-none"
                                >
                                  {{client.title}}
                                </LinkTo>
                              </td>
                              <td>
                                {{#if
                                  (eq client.subscription "trial")
                                }}Trial{{/if}}
                                {{#if
                                  (eq client.subscription "selected")
                                }}Selected Content{{/if}}
                                {{#if
                                  (eq client.subscription "full")
                                }}Standard{{/if}}
                                {{#if
                                  (eq client.subscription "premium")
                                }}Premium{{/if}}
                              </td>
                              <td>
                                {{this.formatDate client.expires}}
                                {{#if (this.isExpired client.expires)}}
                                  <span class="badge bg-danger">Expired</span>
                                {{else if (this.expiresSoon client.expires)}}
                                  <span class="badge bg-warning">Expires Soon</span>
                                {{/if}}
                              </td>
                              <td>
                                {{#if client.domains}}
                                  <div class="mb-1">
                                    <small class="text-muted">Domains:</small>
                                    {{#each client.domains as |domain|}}
                                      <span
                                        class="badge bg-secondary me-1"
                                      >{{domain}}</span>
                                    {{/each}}
                                  </div>
                                {{/if}}
                                {{#if client.connections}}
                                  <div>
                                    <small
                                      class="text-muted"
                                    >Connections:</small>
                                    {{#each client.connections as |connection|}}
                                      <span
                                        class="badge bg-info me-1"
                                      >{{connection}}</span>
                                    {{/each}}
                                  </div>
                                {{/if}}
                              </td>
                            </tr>
                          {{/each}}
                        </tbody>
                      </table>
                    </div>

                    {{! Pagination }}
                    <div class="mt-4 d-flex justify-content-center">
                      <Pagination
                        @page={{this.state.page}}
                        @pageSize={{this.state.pageSize}}
                        @total={{getTotal response.meta.page}}
                        @onPageChange={{this.setPage}}
                      />
                    </div>
                  {{else}}
                    <div class="alert alert-info">
                      No clients found matching your filters.
                    </div>
                  {{/if}}
                </:content>

                <:loading>
                  <div class="text-center py-5">
                    <div class="spinner-border" role="status">
                      <span class="visually-hidden">Loading clients...</span>
                    </div>
                  </div>
                </:loading>

                <:error as |error|>
                  <div class="alert alert-danger">
                    Error loading clients:
                    {{error.message}}
                  </div>
                </:error>
              </Request>
            </:content>
          </SearchClients>
        </:if>
        <:else>
          <div class="alert alert-warning" role="alert">
            <h4 class="alert-heading">Access Denied</h4>
            <p>You do not have permission to view clients.</p>
            <p class="mb-0">Please contact your administrator if you believe you
              should have access.</p>
          </div>
        </:else>
      </IfAbility>
    </div>
  </template>
}
